import { ReactNode } from 'react';
import { Text, Icon } from '@shopify/polaris';
import type { IconProps } from '@shopify/polaris';

type IconSource = IconProps['source'];

interface HelpSectionProps {
  id: string;
  title: string;
  icon?: IconSource;
  color?: string;
  children: ReactNode;
}

export function HelpSection({ id, title, icon, color = '#2c6ecb', children }: HelpSectionProps) {
  return (
    <section
      id={id}
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e1e3e5',
        overflow: 'hidden',
        marginBottom: '24px',
      }}
    >
      {/* Section Header */}
      <div
        style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e1e3e5',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        {icon && (
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            <Icon source={icon} />
          </div>
        )}
        <Text as="h2" variant="headingMd" fontWeight="bold">
          {title}
        </Text>
      </div>

      {/* Section Content */}
      <div style={{ padding: '24px' }}>{children}</div>
    </section>
  );
}

interface StepItemProps {
  number: number;
  title: string;
  description: string;
  color?: string;
}

export function StepItem({ number, title, description, color = '#2c6ecb' }: StepItemProps) {
  return (
    <div
      style={{
        backgroundColor: '#f6f6f7',
        borderRadius: '8px',
        padding: '16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <div
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: color,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '13px',
            fontWeight: 'bold',
            flexShrink: 0,
          }}
        >
          {number}
        </div>
        <div style={{ flex: 1 }}>
          <Text as="span" variant="headingSm" fontWeight="semibold">
            {title}
          </Text>
          <div style={{ marginTop: '4px' }}>
            <Text as="p" tone="subdued">
              {description}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}

interface InfoItemProps {
  icon?: IconSource;
  color?: string;
  label: string;
  description: string;
}

export function InfoItem({ icon, color = '#2c6ecb', label, description }: InfoItemProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
      {icon && (
        <div
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            backgroundColor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color,
            flexShrink: 0,
          }}
        >
          <Icon source={icon} />
        </div>
      )}
      <div style={{ flex: 1 }}>
        <Text as="span" fontWeight="semibold">
          {label}
        </Text>
        <span style={{ color: '#637381' }}> - {description}</span>
      </div>
    </div>
  );
}
